import { DeleteIcon } from "@chakra-ui/icons";
import {
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  FormLabel,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  useToast
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { useData } from "../../Contexts/DataContext";
import { deleteFilesFromUrls, uploadLogo } from "../../Utils/fileStorageUtils";
import ConfirmDialog from "../Shared/ConfirmDialog";

const AddEditClientModal = ({ isOpen, onClose, client }) => {
  const { addDocument, updateDocument, deleteDocument } = useData();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef();
  const toast = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Initialize form state
  const [clientInfo, setClientInfo] = useState({
    name: "",
    abbreviation: "",
    tenantId: "",
    logo: "",
    contactName: "",
    email: "",
  });

  // Update form if editing an existing client
  useEffect(() => {
    if (client) {
      setClientInfo({
        name: client.name || "",
        abbreviation: client.abbreviation || "",
        tenantId: client.tenantId || "",
        logo: client.logo || "",
        contactName: client.contactName || "",
        email: client.email || "",
      });
    }
  }, [client]);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      let logoUrl = clientInfo.logo;

      if (file) {
        // Keep this as-is — assumes uploadLogo() is imported here
        logoUrl = await uploadLogo(file, clientInfo.name);
      }

      const clientData = { ...clientInfo, logo: logoUrl };

      if (client) {
        await updateDocument("clients", client.id, clientData);
        toast({
          title: "Client updated!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        const docId = await addDocument("clients", clientData);
        await updateDocument("clients", docId, { clientId: docId }); // ✅ store docId as clientId
        toast({
          title: "Client added!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }

      onClose();
    } catch (error) {
      console.error("Error saving client:", error);
      toast({
        title: "Error saving client",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setClientInfo((prev) => ({ ...prev, logo: reader.result })); // Update preview
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteClient = async () => {
    try {
      // 🔥 Delete logo if present
      if (client?.logo) {
        await deleteFilesFromUrls([client.logo]);
      }

      // 🗑️ Delete the client document
      await deleteDocument("clients", client?.id);

      toast({
        title: "Client deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error deleting client",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent bg={"gray.50"}>
        <ModalHeader bg={"gray.700"} color={"white"}>
          {client ? "Edit Client" : "Add Client"}
        </ModalHeader>
        <ModalCloseButton color={"white"} />

        <ModalBody>
          <FormControl mt={4}>
            <FormLabel>Client Name</FormLabel>
            <Input
              bg="white"
              size="sm"
              fontSize="sm"
              value={clientInfo.name}
              onChange={(e) =>
                setClientInfo((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </FormControl>

          <FormControl mt={4}>
            <FormLabel>Client Abbreviation</FormLabel>
            <Input
              bg="white"
              size="sm"
              fontSize="sm"
              value={clientInfo.abbreviation}
              onChange={(e) =>
                setClientInfo((prev) => ({
                  ...prev,
                  abbreviation: e.target.value,
                }))
              }
            />
          </FormControl>

          <FormControl mt={4}>
            <FormLabel>Tenant ID</FormLabel>
            <Input
              bg="white"
              size="sm"
              fontSize="sm"
              value={clientInfo.tenantId}
              onChange={(e) =>
                setClientInfo((prev) => ({ ...prev, tenantId: e.target.value }))
              }
            />
          </FormControl>

          <FormControl mt={4}>
            <FormLabel>Client Logo</FormLabel>
            {clientInfo.logo && (
              <Image src={clientInfo.logo} alt="Client Logo" mb={3} />
            )}
            <Input
              bg="transparent"
              border={"none"}
              size="sm"
              fontSize="sm"
              type="file"
              onChange={handleFileChange}
              ref={fileInputRef}
            />
          </FormControl>

          <FormControl mt={4}>
            <FormLabel>Contact Name</FormLabel>
            <Input
              bg="white"
              size="sm"
              fontSize="sm"
              value={clientInfo.contactName}
              onChange={(e) =>
                setClientInfo((prev) => ({
                  ...prev,
                  contactName: e.target.value,
                }))
              }
            />
          </FormControl>

          <FormControl mt={4}>
            <FormLabel>Contact Email</FormLabel>
            <Input
              bg="white"
              size="sm"
              fontSize="sm"
              value={clientInfo.email}
              onChange={(e) =>
                setClientInfo((prev) => ({ ...prev, email: e.target.value }))
              }
            />
          </FormControl>
        </ModalBody>
        <Divider />
        <ModalFooter>
          {client && (
            <Button
              leftIcon={<DeleteIcon />}
              colorScheme="red"
              variant="outline"
              fontSize="xs"
              onClick={() => setShowDeleteDialog(true)}
            >
              Delete
            </Button>
          )}
          <Spacer />

          <ButtonGroup>
            <Button
              mr={3}
              colorScheme="green"
              onClick={handleSubmit}
              isLoading={isSubmitting}
            >
              {client ? "Update" : "Add"}
            </Button>
            <Button variant={"outline"} onClick={onClose}>
              Cancel
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={async () => {
          await handleDeleteClient();
          setShowDeleteDialog(false);
        }}
        title="Confirm Deletion"
        body="Are you sure you want to delete this client? This cannot be undone."
        confirmText="Yes, Delete"
        cancelText="Cancel"
      />
    </Modal>
  );
};

export default AddEditClientModal;
